import { useEffect, useState } from 'react';
import {
  addDeviceOnlyMember,
  createConsentSubmission,
  getConsent,
  getMemberState,
  submitQuiz,
  type MemberGuardian,
} from '../../api/endpoints.js';
import { useAction, useApi, useIntentKey } from '../../api/hooks.js';
import { writeFlags } from '../../flow/machine.js';
import { goHome, goStep } from '../../flow/nav.js';
import { Failure } from '../../ui/kit.js';
import {
  FlowField,
  FlowScreen,
  FlowSub,
  FlowTitle,
  PrimaryButton,
  SecondaryButton,
} from '../../ui/flow.js';
import { EnrolmentCapture } from './selfie.js';
import { QuizStep } from './quiz.js';
import { PhotoBatch } from './extras.js';

/**
 * Mobile screens 18–20 — the family loop, reached only when the purpose was
 * "My family". This is the ON-DEVICE branch: the member is standing next to the
 * manager, so THEY answer their own quiz, THEY take the liveness capture, and
 * the photos are theirs. The manager's session is only the capture context.
 *
 * MINORS ARE SUPPORTED as of 2026-08-26. A date of birth under 18 asks for a
 * guardian of record, and the manager becomes that guardian: the guardianship
 * lands in the same transaction as the person and the seat, and consent is
 * signed by the guardian on a document that names the child. What a minor does
 * NOT get is monitoring — services refuses discovery for anyone under 18, so
 * the consent copy says so rather than implying a scan is running.
 *
 * One mobile step has no /v1 equivalent and is dropped: screen 19's
 * "Additional details" (employer/school for the member) — PATCH /v1/me/profile
 * writes only the session's own person, and no member-profile route exists.
 */

export interface MemberContext {
  personId: string;
  firstName: string;
  lastName: string;
  isMinor: boolean;
}

export function FamilyChoiceStep() {
  const complete = () => {
    writeFlags({ familyComplete: true });
    goHome();
  };

  return (
    <FlowScreen
      progress={97}
      footer={<PrimaryButton onClick={complete}>Complete Setup</PrimaryButton>}
    >
      <FlowTitle>Add additional members to your household?</FlowTitle>
      <FlowSub>
        You can set them up on your phone or invite them to join your household on theirs.
      </FlowSub>
      <div className="option-stack">
        <button type="button" className="choice-row" onClick={() => goStep('family-member')}>
          <span className="choice-glyph" aria-hidden="true">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
              <circle cx="10" cy="8" r="3.4" stroke="#380E99" strokeWidth="1.8" />
              <path d="M4 19c.8-3 3-4.6 6-4.6s5.2 1.6 6 4.6" stroke="#380E99" strokeWidth="1.8" strokeLinecap="round" />
              <path d="M18 6v6M15 9h6" stroke="#380E99" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </span>
          <span className="choice-text">
            <strong>Add a member on this device</strong>
            <span>They&rsquo;ll be monitored from your account, no device of their own</span>
          </span>
          <span className="choice-chevron" aria-hidden="true">
            ›
          </span>
        </button>
        <button type="button" className="choice-row" onClick={complete}>
          <span className="choice-glyph" aria-hidden="true">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 15V4m0 0L7.5 8.5M12 4l4.5 4.5" stroke="#380E99" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M5 13v5a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-5" stroke="#380E99" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </span>
          <span className="choice-text">
            <strong>Invite them to their own device later</strong>
            <span>Send invites anytime from your Dashboard&rsquo;s Family tab</span>
          </span>
          <span className="choice-chevron" aria-hidden="true">
            ›
          </span>
        </button>
      </div>
      <p className="flow-note">
        Children can be added here. You&rsquo;ll be recorded as their parent or guardian and sign
        their consent. Monitoring doesn&rsquo;t run for anyone under 18 yet, so what they get today
        is protection for the photos in your own library.
      </p>
    </FlowScreen>
  );
}

/**
 * PRESENTATION ONLY. profile.is_minor against the age of majority is the
 * decision, server-side; this exists so the guardian fields appear before the
 * round trip rather than after a rejection. A client that disagrees just shows
 * the wrong form and gets a 400 saying so.
 */
function looksMinor(dob: string): boolean {
  const value = dob.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const eighteenth = new Date(value);
  if (Number.isNaN(eighteenth.getTime())) return false;
  eighteenth.setFullYear(eighteenth.getFullYear() + 18);
  return eighteenth > new Date();
}

type MemberStage = 'details' | 'quiz' | 'consent' | 'selfie';

const RELATIONSHIPS: MemberGuardian['relationship'][] = ['parent', 'legal_guardian', 'other'];

export function FamilyMemberStep({
  member,
  onMember,
}: {
  member: MemberContext | null;
  onMember: (member: MemberContext) => void;
}) {
  const [stage, setStage] = useState<MemberStage>(member === null ? 'details' : 'quiz');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [dob, setDob] = useState('');
  const [relationship, setRelationship] = useState<MemberGuardian['relationship']>('parent');
  const [attested, setAttested] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const registerKey = useIntentKey();
  const consentKey = useIntentKey();
  const [consentId, setConsentId] = useState<string | null>(null);
  const isMinor = looksMinor(dob);

  /**
   * A reload used to lose the member and restart the loop. The server knows
   * where they got to — `next` is a hint, and each step still enforces its own
   * gate, so trusting it costs nothing.
   */
  const state = useApi(() => getMemberState(member?.personId ?? ''), [member?.personId], {
    skip: member === null,
  });
  const serverNext = state.data?.next;
  useEffect(() => {
    if (serverNext === 'quiz' || serverNext === 'consent') setStage(serverNext);
    else if (serverNext === 'enrolment') setStage('selfie');
    /**
     * `'photos'` (2026-09-03) MEANS "CONSENT IS SIGNED AND THEY HAVE NO
     * PHOTOS", AND IT SAYS NOTHING ABOUT ENROLMENT — so it resumes at the
     * SELFIE, not at the photo step.
     *
     * The server cannot say otherwise: `nextStepFor` never returns
     * `'enrolment'`, because reading the enrolment state would need a
     * cross-boundary read into `svc.v_person_enrolment_state` that
     * `memberState` does not have (the value stays in the type so adding it
     * later is not a breaking change). Landing on the photo step here would
     * therefore walk straight past the face scan, and a member with no face
     * signature is never monitored — which is the silent failure this whole
     * loop exists to avoid.
     *
     * The selfie step starts idle behind a button, so resuming here costs no
     * liveness attempt, and it offers a way past for a member who has already
     * been scanned.
     */
    else if (serverNext === 'photos') setStage('selfie');
  }, [serverNext]);

  const register = useAction(addDeviceOnlyMember, (result) => {
    onMember({ personId: result.person_id, firstName, lastName, isMinor });
    // Straight to their quiz: they are standing here, so they answer it.
    setStage('quiz');
  });

  const quiz = useAction(submitQuiz, () => {
    setStage('consent');
  });

  const consent = useAction(createConsentSubmission, (result) => {
    setConsentId(result.consent_id);
    window.open(result.signing_url, '_blank', 'noopener,noreferrer');
  });

  const consentStatus = useApi(() => getConsent(consentId ?? ''), [consentId], {
    skip: consentId === null,
    pollMs: 2000,
  });
  const signed = consentStatus.data?.status === 'signed';
  useEffect(() => {
    if (signed) setStage('selfie');
  }, [signed]);

  const submitDetails = () => {
    if (firstName.trim().length === 0 || lastName.trim().length === 0) {
      setProblem('Please enter both first and last name.');
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dob.trim())) {
      setProblem('Please enter the date of birth as YYYY-MM-DD.');
      return;
    }
    if (isMinor) {
      // NO NAME ASKED FOR any more (2026-09-01): the guardian is whoever is
      // signed in, and the server reads their name from their own person row.
      if (!attested) {
        setProblem('Please confirm you have the legal authority to consent on their behalf.');
        return;
      }
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setProblem('Please enter a valid email address for their consent record.');
      return;
    }
    setProblem(null);
    register.run(
      {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        date_of_birth: dob.trim(),
        // A minor's signer is their guardian, so no address of their own is
        // required — and a young child may have none.
        ...(isMinor ? {} : { email: email.trim() }),
        // Relationship only. The name is the server's to fill in, and sending
        // one here would let this form disagree with the identity it records.
        ...(isMinor ? { guardian: { relationship } } : {}),
      },
      registerKey.key,
    );
  };

  if (stage === 'details') {
    return (
      <FlowScreen
        progress={98}
        onBack={() => window.history.back()}
        footer={
          <PrimaryButton onClick={submitDetails} disabled={register.pending}>
            {register.pending ? 'Adding…' : 'Continue'}
          </PrimaryButton>
        }
      >
        <FlowTitle>Add a member to your household</FlowTitle>
        <FlowSub>
          They&rsquo;ll be registered on your plan and monitored from this account. Hand them the
          phone after this — the next steps are theirs to answer.
        </FlowSub>
        {problem !== null && <p className="flow-error">{problem}</p>}
        <div className="field-stack">
          <FlowField
            label="First Name"
            value={firstName}
            onChange={(value) => {
              setFirstName(value);
              setProblem(null);
              registerKey.next();
            }}
            placeholder="Jane"
            disabled={register.pending}
          />
          <FlowField
            label="Last Name"
            value={lastName}
            onChange={(value) => {
              setLastName(value);
              setProblem(null);
              registerKey.next();
            }}
            placeholder="Doe"
            disabled={register.pending}
          />
          <FlowField
            label="Date of Birth"
            value={dob}
            onChange={(value) => {
              setDob(value);
              setProblem(null);
              registerKey.next();
            }}
            placeholder="YYYY-MM-DD"
            disabled={register.pending}
          />
          {!isMinor && (
            <FlowField
              label="Email"
              value={email}
              onChange={(value) => {
                setEmail(value);
                setProblem(null);
                registerKey.next();
              }}
              placeholder="jane.doe@example.com"
              type="email"
              disabled={register.pending}
            />
          )}
          {isMinor && (
            <>
              <p className="flow-note">
                They&rsquo;re under 18, so you&rsquo;ll be recorded as their parent or guardian
                &mdash; under the name on your own profile &mdash; and you sign their consent.
                They don&rsquo;t need an email address of their own.
              </p>
              <label className="field-label" htmlFor="guardian-relationship">
                Relationship
              </label>
              <select
                id="guardian-relationship"
                value={relationship}
                disabled={register.pending}
                onChange={(event) => {
                  setRelationship(event.target.value as MemberGuardian['relationship']);
                  registerKey.next();
                }}
              >
                {RELATIONSHIPS.map((value) => (
                  <option key={value} value={value}>
                    {value === 'legal_guardian' ? 'Legal guardian' : value === 'parent' ? 'Parent' : 'Other'}
                  </option>
                ))}
              </select>
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={attested}
                  disabled={register.pending}
                  onChange={(event) => {
                    setAttested(event.target.checked);
                    setProblem(null);
                  }}
                />
                <span>
                  I am this child&rsquo;s parent or legal guardian and I have the legal authority to
                  give consent to biometric processing on their behalf.
                </span>
              </label>
            </>
          )}
        </div>
        {register.error !== null && (
          <Failure
            error={register.error}
            hint={
              {
                VALIDATION_FAILED:
                  'Check the date of birth. Under 18 needs a parent or guardian; 18 and over needs their own email address and no guardian.',
                UNDER_MIN_AGE:
                  'They are below the minimum age this backend will enrol. That floor is a config value — ask an operator what it is set to.',
                DOB_REQUIRED:
                  'Your own date of birth is missing, and we cannot record you as a guardian without it. Add it on your profile first.',
                FORBIDDEN: 'A guardian has to be an adult.',
                HOUSEHOLD_PLAN_REQUIRED:
                  'Adding members needs a household-tier plan. Upgrade from the Dashboard, then come back.',
                HOUSEHOLD_FULL: 'Every seat on the plan is taken.',
                EMAIL_IN_USE: 'That email already belongs to a person.',
              }[register.error.code]
            }
          />
        )}
      </FlowScreen>
    );
  }

  if (member === null) {
    // Landed here without a member (reload mid-loop): back to the choice.
    goStep('family-choice', { replace: true });
    return null;
  }

  if (stage === 'quiz') {
    return (
      <>
        <FlowScreen progress={98}>
          <FlowTitle>
            {member.firstName}&rsquo;s questions
          </FlowTitle>
          <FlowSub>
            {member.isMinor
              ? `A few questions about ${member.firstName}. Answer them with them if they are too young to answer for themselves — the answers are recorded as theirs.`
              : `Hand ${member.firstName} the phone. These answers are theirs, and they set their own likeness health score.`}
          </FlowSub>
          {quiz.error !== null && <Failure error={quiz.error} />}
        </FlowScreen>
        <QuizStep
          onComplete={(quizVersion, answers) => {
            quiz.run(quizVersion, answers, member.personId);
          }}
        />
      </>
    );
  }

  if (stage === 'consent') {
    return (
      <FlowScreen
        progress={98}
        footer={
          <PrimaryButton
            onClick={() => consent.run(member.personId, consentKey.key)}
            disabled={consent.pending || (consentId !== null && !signed)}
          >
            {consent.pending
              ? 'Opening consent…'
              : consentId !== null
                ? 'Waiting for the signature…'
                : member.isMinor
                  ? 'Open their consent form to sign'
                  : 'Open their consent form'}
          </PrimaryButton>
        }
      >
        <FlowTitle>
          Consent for {member.firstName} {member.lastName}
        </FlowTitle>
        <FlowSub>
          {member.isMinor
            ? `You sign this one, as ${member.firstName}'s parent or guardian. It names them as the person being enrolled. Monitoring does not run for anyone under 18 — their face signature is created and their photos are protected, and no search of monitored sources happens for them.`
            : 'Their biometric consent must be signed before their face is scanned. The form opens in its own tab; this screen continues by itself once the signature is recorded.'}
        </FlowSub>
        {consent.error !== null && (
          <Failure
            error={consent.error}
            hint={
              {
                DOB_REQUIRED: 'The member has no date of birth on record.',
                GUARDIANSHIP_REQUIRED:
                  'A minor subject needs a guardian on record before consent can be collected.',
                GUARDIANSHIP_UNAVAILABLE: 'No eligible guardian exists in this household.',
                FORBIDDEN:
                  'Only the guardian recorded for this child can sign their consent.',
                SUBJECT_EMAIL_UNVERIFIED:
                  'The member must verify their email before consent can be created — they were sent a verification mail when you added them.',
              }[consent.error.code]
            }
          />
        )}
        {consentStatus.error !== null && <Failure error={consentStatus.error} />}
      </FlowScreen>
    );
  }

  return (
    <FlowScreen progress={98}>
      <FlowTitle>
        {member.firstName}&rsquo;s face scan
      </FlowTitle>
      <FlowSub>
        Ask {member.firstName} to look at the camera. The check streams securely to the face-scan
        service and creates their facial signature.
      </FlowSub>
      <EnrolmentCapture
        subjectPersonId={member.personId}
        startLabel="Capture their selfie"
        onEnrolled={() => goStep('family-photos')}
      />
      {/* THE ONE STEP THE SERVER CANNOT CONFIRM FROM HERE, so the manager is
          asked rather than guessed at. `next` skips from consent to 'photos'
          with no enrolment value in between, so a resumed loop lands here
          whether or not the scan has already happened. Their enrolment status
          is on the Family roster, and on the member screen. */}
      <SecondaryButton onClick={() => goStep('family-photos')}>
        They&rsquo;ve already been scanned &mdash; go to their photos
      </SecondaryButton>
    </FlowScreen>
  );
}

export function FamilyPhotosStep({ member }: { member: MemberContext | null }) {
  const name = member === null ? 'them' : member.firstName;
  return (
    <FlowScreen progress={99} onBack={() => window.history.back()}>
      <FlowTitle>Photos</FlowTitle>
      <FlowSub>
        Add up to 50 photos that feature {name}&rsquo;s face that they&rsquo;ve recently shared on
        social media. Include their profile pictures. We&rsquo;ll monitor for their use and abuse
        online.
      </FlowSub>
      <p className="flow-note">
        These go in {name}&rsquo;s own space, against their own allowance &mdash; not yours. The
        first one to finish processing also becomes their profile picture.
      </p>
      <PhotoBatch
        subject="member"
        // THEIR space, THEIR allowance. Sent on both the mint and the commit
        // (uploadPhotos threads it through), because the api authorises it on
        // each and a commit that omitted it would file these under the manager.
        subjectPersonId={member?.personId}
        onDone={() => goStep('family-choice', { replace: true })}
        onSkip={() => goStep('family-choice', { replace: true })}
      />
    </FlowScreen>
  );
}
